import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import printServerApi, { 
  PrintQueueStatus, 
  ClearQueueResult, 
  Organization,
  QueueDetails,
  QueueList
} from '../../services/printServer'

interface ActivityLog {
  timestamp: string
  action: string
  details: string
  status: 'success' | 'error' | 'info'
  user?: string
}

interface ClearingProgress {
  currentItem: number
  totalItems: number
  currentItemName?: string
  percentage: number
  isProcessing: boolean
}

interface PrintServerState {
  organizations: Organization[]
  selectedOrganization: string | null
  queueStatus: PrintQueueStatus | null
  queueDetails: QueueDetails | null
  isLoading: boolean
  isLoadingDetails: boolean
  isClearing: boolean
  clearingProgress: ClearingProgress | null
  error: string | null
  lastClearResult: ClearQueueResult | null
  activityLogs: ActivityLog[]
  isConfigured: boolean
}

const initialState: PrintServerState = {
  organizations: [],
  selectedOrganization: null,
  queueStatus: null,
  queueDetails: null,
  isLoading: false,
  isLoadingDetails: false,
  isClearing: false,
  clearingProgress: null,
  error: null,
  lastClearResult: null,
  activityLogs: [],
  isConfigured: false,
}

// Async thunks
export const fetchOrganizations = createAsyncThunk(
  'printServer/fetchOrganizations',
  async () => {
    const organizations = await printServerApi.getOrganizations()
    return organizations
  }
)

export const checkQueueStatus = createAsyncThunk(
  'printServer/checkQueueStatus',
  async (organizationCode: string) => {
    const status = await printServerApi.checkQueueStatus(organizationCode)
    return status
  }
)

export const fetchQueueDetails = createAsyncThunk(
  'printServer/fetchQueueDetails',
  async (organizationCode: string) => {
    const details = await printServerApi.getQueueDetails(organizationCode)
    return details
  }
)

export const clearPrintQueue = createAsyncThunk(
  'printServer/clearPrintQueue',
  async ({ organizationCode, dryRun = false }: { 
    organizationCode: string
    dryRun?: boolean 
  }, { dispatch }) => {
    const result = await printServerApi.clearPrintQueue(
      organizationCode, 
      dryRun,
      (progress) => {
        dispatch(printServerSlice.actions.updateClearingProgress(progress))
      }
    )
    return result
  }
)

export const checkConfiguration = createAsyncThunk(
  'printServer/checkConfiguration',
  async () => {
    const secrets = await printServerApi.getSecrets()
    return secrets
  }
)

const printServerSlice = createSlice({
  name: 'printServer',
  initialState,
  reducers: {
    setSelectedOrganization: (state, action: PayloadAction<string>) => {
      state.selectedOrganization = action.payload
      // Clear previous status when switching organizations
      state.queueStatus = null
      state.queueDetails = null
      state.lastClearResult = null
    },
    clearError: (state) => {
      state.error = null
    },
    updateClearingProgress: (state, action: PayloadAction<ClearingProgress>) => {
      state.clearingProgress = action.payload
    },
    resetClearingProgress: (state) => {
      state.clearingProgress = null
    },
    addActivityLog: (state, action: PayloadAction<Omit<ActivityLog, 'timestamp'>>) => {
      state.activityLogs.unshift({
        ...action.payload,
        timestamp: new Date().toISOString(),
      })
      // Keep only last 50 logs
      if (state.activityLogs.length > 50) {
        state.activityLogs = state.activityLogs.slice(0, 50)
      }
    },
    clearActivityLogs: (state) => {
      state.activityLogs = []
    },
  },
  extraReducers: (builder) => {
    // Fetch organizations
    builder
      .addCase(fetchOrganizations.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchOrganizations.fulfilled, (state, action) => {
        state.isLoading = false
        state.organizations = action.payload
        // Auto-select first organization if none selected
        if (!state.selectedOrganization && action.payload.length > 0) {
          state.selectedOrganization = action.payload[0].code
        }
      })
      .addCase(fetchOrganizations.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to fetch organizations'
      })

    // Check queue status
    builder
      .addCase(checkQueueStatus.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(checkQueueStatus.fulfilled, (state, action) => {
        state.isLoading = false
        state.queueStatus = action.payload
        state.activityLogs.unshift({
          timestamp: new Date().toISOString(),
          action: 'Queue Status Checked',
          details: `Found ${action.payload.unprintedItems} unprinted items`,
          status: 'info',
        })
      })
      .addCase(checkQueueStatus.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to check queue status'
        state.activityLogs.unshift({
          timestamp: new Date().toISOString(),
          action: 'Queue Status Check Failed',
          details: action.error.message || 'Unknown error',
          status: 'error',
        })
      })

    // Fetch queue details
    builder
      .addCase(fetchQueueDetails.pending, (state) => {
        state.isLoadingDetails = true
        state.error = null
      })
      .addCase(fetchQueueDetails.fulfilled, (state, action) => {
        state.isLoadingDetails = false
        state.queueDetails = action.payload
        const totalItems = action.payload.data.reduce((sum, list) => sum + list.items.length, 0)
        const unprintedItems = action.payload.data.reduce((sum, list) => 
          sum + list.items.filter(item => item.is_printed === 0).length, 0
        )
        state.activityLogs.unshift({
          timestamp: new Date().toISOString(),
          action: 'Queue Details Fetched',
          details: `Found ${action.payload.data.length} lists with ${totalItems} total items (${unprintedItems} unprinted)`,
          status: 'info',
        })
      })
      .addCase(fetchQueueDetails.rejected, (state, action) => {
        state.isLoadingDetails = false
        state.error = action.error.message || 'Failed to fetch queue details'
        state.activityLogs.unshift({
          timestamp: new Date().toISOString(),
          action: 'Queue Details Fetch Failed',
          details: action.error.message || 'Unknown error',
          status: 'error',
        })
      })

    // Clear print queue
    builder
      .addCase(clearPrintQueue.pending, (state) => {
        state.isClearing = true
        state.error = null
        state.clearingProgress = {
          currentItem: 0,
          totalItems: 0,
          percentage: 0,
          isProcessing: true
        }
      })
      .addCase(clearPrintQueue.fulfilled, (state, action) => {
        state.isClearing = false
        state.clearingProgress = null
        state.lastClearResult = action.payload
        state.activityLogs.unshift({
          timestamp: new Date().toISOString(),
          action: action.payload.dryRun ? 'Dry Run Completed' : 'Queue Cleared',
          details: action.payload.message,
          status: 'success',
        })
        // Refresh queue status after clearing
        state.queueStatus = null
        state.queueDetails = null
      })
      .addCase(clearPrintQueue.rejected, (state, action) => {
        state.isClearing = false
        state.clearingProgress = null
        state.error = action.error.message || 'Failed to clear print queue'
        state.activityLogs.unshift({
          timestamp: new Date().toISOString(),
          action: 'Queue Clear Failed',
          details: action.error.message || 'Unknown error',
          status: 'error',
        })
      })

    // Check configuration
    builder
      .addCase(checkConfiguration.fulfilled, (state, action) => {
        state.isConfigured = action.payload.isConfigured
        if (action.payload.availableCodes && action.payload.availableCodes.length > 0) {
          state.organizations = action.payload.availableCodes
        }
      })
  },
})

export const { 
  setSelectedOrganization, 
  clearError,
  updateClearingProgress,
  resetClearingProgress,
  addActivityLog,
  clearActivityLogs 
} = printServerSlice.actions

export default printServerSlice.reducer